import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, Home, Calendar, DollarSign, Star, Settings, Eye, Edit, Trash2, ChevronRight, Building, Award, TrendingUp, CalendarDays, Car, AlertTriangle, Shield, FileText, BarChart3, Zap, Clock, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Navbar } from '@/components/Navbar';
import { AvailabilityCalendar } from '@/components/AvailabilityCalendar';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const HostDashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Unauthorized parking report state
  const [reportDialog, setReportDialog] = useState({ open: false, listingId: null });
  const [reportForm, setReportForm] = useState({ plate: '', description: '', notes: '' });
  const [checkingPlate, setCheckingPlate] = useState(false);
  const [plateCheckResult, setPlateCheckResult] = useState(null);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [listingsRes, bookingsRes, earningsRes, payoutsRes, bankRes] = await Promise.all([
          axios.get(`${API}/my-listings`, { withCredentials: true, headers }),
          axios.get(`${API}/host-bookings`, { withCredentials: true, headers }),
          axios.get(`${API}/earnings`, { withCredentials: true, headers }),
          axios.get(`${API}/payouts`, { withCredentials: true, headers }),
          axios.get(`${API}/bank-account`, { withCredentials: true, headers })
        ]);
        
        setListings(listingsRes.data);
        setBookings(bookingsRes.data);
        setEarnings(earningsRes.data);
        setPayouts(payoutsRes.data);
        setBankAccount(bankRes.data);
        
        // Fetch analytics
        try {
          const analyticsRes = await axios.get(`${API}/host/analytics`, { withCredentials: true, headers });
          setAnalytics(analyticsRes.data);
        } catch (e) { console.log('Analytics not available yet'); }

        // Fetch wallet
        try {
          const walletRes = await axios.get(`${API}/host/wallet`, { withCredentials: true, headers });
          setWallet(walletRes.data);
        } catch (e) { console.log('Wallet not available yet'); }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refreshWallet = async () => {
    try {
      const walletRes = await axios.get(`${API}/host/wallet`, { withCredentials: true, headers: getAuthHeaders() });
      setWallet(walletRes.data);
    } catch (e) { /* noop */ }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      const res = await axios.post(`${API}/host/wallet/withdraw`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(`Withdrew $${res.data.withdrawal.amount.toFixed(2)} to your bank.`);
      await refreshWallet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleToggleAutoWithdraw = async (checked) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/host/wallet/settings`, { auto_withdraw_enabled: checked }, { withCredentials: true, headers: getAuthHeaders() });
      setWallet(w => ({ ...w, auto_withdraw_enabled: checked }));
      toast.success(checked ? 'Auto-withdraw turned on' : 'Auto-withdraw turned off');
      if (checked) {
        // Trigger auto-withdraw by refetching (backend auto-withdraws on fetch if conditions met)
        await refreshWallet();
      }
    } catch (err) {
      toast.error('Failed to update setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleConnectBank = () => {
    // Take the user to Profile -> Bank section so they can complete connection there
    navigate('/profile?focus=bank#bank-section');
  };

  const handleHostCheckout = async (bookingId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/bookings/${bookingId}/checkout`, {}, { withCredentials: true, headers });
      toast.success("Marked driver as checked out — overstay fees won't apply.");
      setBookings(prev => prev.map(b =>
        b.booking_id === bookingId
          ? { ...b, checked_out_at: res.data.checked_out_at, checked_out_by: res.data.checked_out_by }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark checkout');
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/bookings/${bookingId}/status?status=${action}`, {}, { withCredentials: true, headers });
      toast.success(`Booking ${action}`);
      // Refresh bookings
      const res = await axios.get(`${API}/host-bookings`, { withCredentials: true, headers });
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/bookings/${bookingId}/complete`, {}, { withCredentials: true, headers });
      toast.success('Booking marked as completed');
      const res = await axios.get(`${API}/host-bookings`, { withCredentials: true, headers });
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to complete booking');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to take down this listing? It will no longer be visible to drivers.')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/listings/${listingId}`, { withCredentials: true, headers });
      toast.success('Listing taken down');
      setListings(listings.filter(l => l.listing_id !== listingId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to take down listing');
    }
  };

  const handlePublish = async (listingId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/listings/${listingId}`, { status: 'published' }, { withCredentials: true, headers });
      toast.success('Listing published!');
      const res = await axios.get(`${API}/my-listings`, { withCredentials: true, headers });
      setListings(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish listing');
    }
  };

  // Check if a vehicle plate is authorized
  const handleCheckPlate = async () => {
    if (!reportForm.plate.trim() || !reportDialog.listingId) {
      toast.error('Please enter a plate number');
      return;
    }
    
    setCheckingPlate(true);
    setPlateCheckResult(null);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(
        `${API}/unauthorized-parking/check/${reportDialog.listingId}/${encodeURIComponent(reportForm.plate)}`,
        { withCredentials: true, headers }
      );
      setPlateCheckResult(res.data);
      if (res.data.authorized) {
        toast.success('This vehicle is authorized!');
      } else {
        toast.warning('This vehicle is NOT authorized');
      }
    } catch (error) {
      toast.error('Failed to check plate');
    } finally {
      setCheckingPlate(false);
    }
  };

  // Report unauthorized parking
  const handleReportUnauthorized = async () => {
    if (!reportForm.plate.trim()) {
      toast.error('Please enter the vehicle plate');
      return;
    }
    
    setSubmittingReport(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/unauthorized-parking/report`, {
        listing_id: reportDialog.listingId,
        vehicle_plate: reportForm.plate.toUpperCase(),
        vehicle_description: reportForm.description,
        notes: reportForm.notes
      }, { withCredentials: true, headers });
      
      if (res.data.authorized) {
        toast.success('Good news! This vehicle actually has a valid booking.');
      } else {
        toast.success('Unauthorized parking reported');
        if (res.data.repeat_offender) {
          toast.warning(`This is a repeat offender with ${res.data.previous_reports} previous reports!`);
        }
        if (res.data.towing_recommended) {
          toast.info('Consider towing - this vehicle has been reported multiple times');
        }
      }
      
      setReportDialog({ open: false, listingId: null });
      setReportForm({ plate: '', description: '', notes: '' });
      setPlateCheckResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      pending_payment: 'bg-orange-100 text-orange-700',
      confirmed: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-500',
      draft: 'bg-slate-100 text-slate-700',
      published: 'bg-green-50 text-green-700',
      processing: 'bg-blue-50 text-blue-700',
      paid: 'bg-green-50 text-green-700',
      failed: 'bg-red-50 text-red-700'
    };
    const labels = {
      pending: 'Pending',
      pending_payment: 'Awaiting Payment',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      expired: 'Expired'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getVerificationBadge = (vStatus) => {
    if (!vStatus || vStatus === 'not_submitted') return null;
    const map = {
      pending_verification: { className: 'bg-amber-100 text-amber-700', label: 'Under Review' },
      verified: { className: 'bg-emerald-100 text-emerald-700', label: 'Verified' },
      rejected: { className: 'bg-red-100 text-red-700', label: 'Verification Rejected' }
    };
    const info = map[vStatus];
    if (!info) return null;
    return <Badge className={info.className}><Shield className="w-3 h-3 mr-1" />{info.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <div className="pt-20 flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <div className="pt-24 pb-12">
        <div className="container-main">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Host Dashboard
              </h1>
              <div className="flex items-center gap-2 text-slate-600">
                <span>Welcome back, {user?.name}</span>
                {user?.is_early_adopter && (
                  <Badge className="badge-early-adopter">
                    <Award className="w-3 h-3 mr-1" />
                    Early Host
                  </Badge>
                )}
              </div>
            </div>
            <Button
              onClick={() => navigate('/create-listing')}
              className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
              data-testid="create-listing-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Listing
            </Button>
          </div>

          {/* Quick Link to Driver Bookings */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-800">
                Looking for parking spots you've booked as a driver?
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/driver-bookings')}
              className="text-blue-600 border-blue-300 hover:bg-blue-100"
              data-testid="view-driver-bookings-btn"
            >
              View My Driver Bookings
            </Button>
          </div>

          {/* Bank Account Alert */}
          {!bankAccount && (
            <Alert className="mb-6 border-amber-200 bg-amber-50">
              <DollarSign className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Add your bank account details to receive payouts.{' '}
                <Button variant="link" onClick={() => navigate('/profile')} className="p-0 h-auto text-amber-600 hover:text-amber-700">
                  Set up now
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Stats Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-[#5ebc67]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{listings.length}</p>
                    <p className="text-sm text-slate-500">Active Listings</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {bookings.filter(b => ['pending', 'pending_payment'].includes(b.status)).length}
                    </p>
                    <p className="text-sm text-slate-500">Pending Bookings</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${earnings?.total?.toFixed(2) || '0.00'}</p>
                    <p className="text-sm text-slate-500">Total Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${earnings?.pending?.toFixed(2) || '0.00'}</p>
                    <p className="text-sm text-slate-500">Pending Payout</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
              <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
              <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet</TabsTrigger>
            </TabsList>

            {/* Listings Tab */}
            <TabsContent value="listings">
              {listings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No listings yet</h3>
                    <p className="text-slate-500 mb-6">Create your first listing to start earning</p>
                    <Button onClick={() => navigate('/create-listing')} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Listing
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {listings.map(listing => (
                    <Card key={listing.listing_id} className="overflow-hidden" data-testid={`listing-${listing.listing_id}`}>
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-48 h-32 md:h-auto">
                            <img
                              src={listing.photos?.[0] || 'https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=300'}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 p-4 md:p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-900">{listing.title}</h3>
                                  {getStatusBadge(listing.status)}
                                  {getVerificationBadge(listing.verification_status)}
                                  {listing.is_early_adopter && (
                                    <Badge className="badge-early-adopter text-xs">
                                      <Award className="w-3 h-3" />
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">{listing.address}, {listing.suburb}</p>
                                {listing.verification_status === 'rejected' && listing.verification_rejection_reason && (
                                  <p className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded">
                                    Rejection reason: {listing.verification_rejection_reason}
                                  </p>
                                )}
                                <p className="text-lg font-bold text-[#5ebc67] mt-2">${listing.price_hourly}/hr</p>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
                                {listing.status === 'draft' && (
                                  <Button size="sm" onClick={() => handlePublish(listing.listing_id)} className="bg-[#5ebc67] hover:bg-[#4da855]">
                                    Publish
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => navigate(`/listing/${listing.listing_id}`)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => navigate(`/edit-listing/${listing.listing_id}`)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteListing(listing.listing_id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 hover:text-amber-700 border-amber-300"
                                  onClick={() => {
                                    setReportDialog({ open: true, listingId: listing.listing_id });
                                    setReportForm({ plate: '', description: '', notes: '' });
                                    setPlateCheckResult(null);
                                  }}
                                  data-testid={`report-parking-btn-${listing.listing_id}`}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  <span className="hidden sm:inline">Report</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              {bookings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
                    <p className="text-slate-500">Bookings will appear here when drivers book your spots</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {bookings.map(booking => (
                    <Card key={booking.booking_id} data-testid={`booking-${booking.booking_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{booking.listing?.title}</h3>
                              {getStatusBadge(booking.status)}
                            </div>
                            <p className="text-sm text-slate-500 mb-2">
                              {format(new Date(booking.start_time), 'MMM d, yyyy h:mm a')} - {format(new Date(booking.end_time), 'h:mm a')}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <span>Driver: {booking.driver?.name}</span>
                              <span>Vehicle: {booking.vehicle_size}</span>
                            </div>
                            {(booking.overstay_total || 0) > 0 && (
                              <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                                <AlertTriangle className="w-3 h-3" />
                                Overstay: ${booking.overstay_total.toFixed(2)} (your share auto-credited to wallet)
                              </div>
                            )}
                            {booking.checked_out_at && (
                              <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                                Checked out by {booking.checked_out_by || 'driver'}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-[#5ebc67]">${booking.host_earnings?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">Your earnings (after 5% fee)</p>
                            {booking.base_price && (
                              <p className="text-xs text-slate-400">Base: ${booking.base_price?.toFixed(2)}</p>
                            )}
                            <div className="flex gap-2 mt-2">
                              {booking.status === 'pending' && (
                                <>
                                  <Button size="sm" className="bg-[#5ebc67] hover:bg-[#4da855]" onClick={() => handleBookingAction(booking.booking_id, 'confirmed')}>
                                    Confirm
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleBookingAction(booking.booking_id, 'cancelled')}>
                                    Decline
                                  </Button>
                                </>
                              )}
                              {booking.status === 'confirmed' && !booking.checked_out_at && (
                                <Button
                                  size="sm"
                                  className="bg-[#5ebc67] hover:bg-[#4da855]"
                                  onClick={() => handleHostCheckout(booking.booking_id)}
                                  data-testid={`host-checkout-${booking.booking_id}`}
                                >
                                  Driver has left
                                </Button>
                              )}
                              {booking.status === 'confirmed' && (
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => handleCompleteBooking(booking.booking_id)}>
                                  Mark Complete
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              {!analytics || analytics.total_bookings === 0 ? (
                <Card data-testid="analytics-empty-state">
                  <CardContent className="py-12 text-center">
                    <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No analytics yet</h3>
                    <p className="text-slate-500">Insights will appear once you have completed bookings</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6" data-testid="analytics-content">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card data-testid="kpi-total-earnings">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <DollarSign className="w-5 h-5 text-[#5ebc67]" />
                          <Badge variant="outline" className="text-[10px]">All-time</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Total Earnings</p>
                        <p className="text-2xl font-bold text-slate-900">${analytics.total_earnings.toFixed(2)}</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="kpi-total-bookings">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px]",
                              analytics.demand_trend === 'increasing' && "border-green-500 text-green-700",
                              analytics.demand_trend === 'decreasing' && "border-red-500 text-red-700"
                            )}
                          >
                            {analytics.demand_trend}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Total Bookings</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.total_bookings}</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="kpi-occupancy">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <TrendingUp className="w-5 h-5 text-purple-600" />
                          <Badge variant="outline" className="text-[10px]">30 days</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Occupancy Rate</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.occupancy_rate}%</p>
                      </CardContent>
                    </Card>
                    <Card data-testid="kpi-avg-duration">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <Clock className="w-5 h-5 text-orange-500" />
                          <Badge variant="outline" className="text-[10px]">Avg</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Booking Duration</p>
                        <p className="text-2xl font-bold text-slate-900">{analytics.avg_booking_duration}h</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Earnings by Week */}
                  <Card data-testid="chart-earnings-week">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[#5ebc67]" />
                        Weekly Earnings (Last 8 Weeks)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const maxEarnings = Math.max(...analytics.earnings_by_week.map(w => w.earnings), 1);
                        return (
                          <div className="space-y-2">
                            {analytics.earnings_by_week.map((w, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <div className="w-16 text-xs text-slate-500 shrink-0">{w.week_start}</div>
                                <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                                  <div
                                    className="bg-gradient-to-r from-[#5ebc67] to-[#4da855] h-full rounded-full flex items-center justify-end pr-2 transition-all"
                                    style={{ width: `${Math.max(2, (w.earnings / maxEarnings) * 100)}%` }}
                                  >
                                    {w.earnings > 0 && (
                                      <span className="text-[11px] font-semibold text-white">${w.earnings.toFixed(0)}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Peak Hours */}
                    <Card data-testid="chart-peak-hours">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          Peak Booking Hours
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const maxBookings = Math.max(...analytics.peak_hours.map(h => h.bookings), 1);
                          return (
                            <div className="flex items-end gap-1 h-32">
                              {analytics.peak_hours.map((h) => (
                                <div key={h.hour} className="flex-1 flex flex-col items-center gap-1 group relative">
                                  <div
                                    className="w-full bg-gradient-to-t from-[#5ebc67] to-[#a4dba8] rounded-t hover:opacity-80 transition-opacity"
                                    style={{ height: `${Math.max(2, (h.bookings / maxBookings) * 100)}%` }}
                                    title={`${h.hour}:00 — ${h.bookings} bookings`}
                                  />
                                  {h.hour % 6 === 0 && (
                                    <span className="text-[9px] text-slate-400">{h.hour}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        <p className="text-[11px] text-slate-400 mt-2">Hours of the day (0–23)</p>
                      </CardContent>
                    </Card>

                    {/* Peak Days */}
                    <Card data-testid="chart-peak-days">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-blue-500" />
                          Bookings by Day
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const maxDayBookings = Math.max(...analytics.peak_days.map(d => d.bookings), 1);
                          return (
                            <div className="space-y-2">
                              {analytics.peak_days.map((d) => (
                                <div key={d.day} className="flex items-center gap-3">
                                  <div className="w-10 text-xs font-medium text-slate-600">{d.day}</div>
                                  <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                                    <div
                                      className="bg-blue-500 h-full rounded-full"
                                      style={{ width: `${Math.max(2, (d.bookings / maxDayBookings) * 100)}%` }}
                                    />
                                  </div>
                                  <div className="w-8 text-xs text-slate-500 text-right">{d.bookings}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Per-listing performance */}
                  <Card data-testid="listings-performance">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-700" />
                        Performance by Listing
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.listings_performance.length === 0 ? (
                        <p className="text-sm text-slate-500">No listings to compare yet.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left text-slate-500">
                                <th className="py-2 pr-4 font-medium">Listing</th>
                                <th className="py-2 px-4 font-medium text-right">Bookings</th>
                                <th className="py-2 px-4 font-medium text-right">Earnings</th>
                                <th className="py-2 pl-4 font-medium text-right">Avg Surge</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.listings_performance.map((lp) => (
                                <tr key={lp.listing_id} className="border-b last:border-0 hover:bg-slate-50">
                                  <td className="py-3 pr-4 font-medium text-slate-900 truncate max-w-[260px]">{lp.title}</td>
                                  <td className="py-3 px-4 text-right text-slate-700">{lp.bookings}</td>
                                  <td className="py-3 px-4 text-right text-[#5ebc67] font-semibold">${lp.earnings.toFixed(2)}</td>
                                  <td className="py-3 pl-4 text-right">
                                    <Badge variant="outline" className={cn(
                                      "text-[11px]",
                                      lp.avg_multiplier > 1.1 && "border-orange-400 text-orange-700",
                                      lp.avg_multiplier < 0.95 && "border-blue-400 text-blue-700"
                                    )}>
                                      {lp.avg_multiplier}×
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability">
              {listings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No listings yet</h3>
                    <p className="text-slate-500 mb-6">Create a listing first to manage availability</p>
                    <Button onClick={() => navigate('/create-listing')} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Listing
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Listing Selector */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-slate-700">Select Listing:</label>
                        <Select
                          value={selectedListingId || listings[0]?.listing_id}
                          onValueChange={setSelectedListingId}
                        >
                          <SelectTrigger className="w-[300px]" data-testid="listing-selector">
                            <SelectValue placeholder="Select a listing" />
                          </SelectTrigger>
                          <SelectContent>
                            {listings.map(listing => (
                              <SelectItem key={listing.listing_id} value={listing.listing_id}>
                                {listing.title} - {listing.suburb}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Calendar */}
                  <AvailabilityCalendar
                    listingId={selectedListingId || listings[0]?.listing_id}
                    hourlyRate={listings.find(l => l.listing_id === (selectedListingId || listings[0]?.listing_id))?.price_hourly}
                    onUpdate={() => {
                      // Refresh data after availability update
                    }}
                  />
                </div>
              )}
            </TabsContent>

            {/* Wallet Tab */}
            <TabsContent value="wallet">
              {!wallet ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="w-8 h-8 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin mx-auto" />
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6" data-testid="wallet-content">
                  {/* Available balance — hero card */}
                  <Card className="bg-gradient-to-br from-[#5ebc67] to-[#4da855] text-white border-0 shadow-xl" data-testid="wallet-available-card">
                    <CardContent className="p-6 sm:p-8">
                      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                        <div>
                          <p className="text-sm text-white/80 uppercase tracking-wider font-medium mb-2">Available to withdraw</p>
                          <p className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="wallet-available-amount">
                            ${wallet.available.toFixed(2)}
                          </p>
                          {wallet.auto_withdraw_enabled && wallet.bank_connected && (
                            <p className="text-xs text-white/80 mt-2 flex items-center gap-1">
                              <Zap className="w-3 h-3" /> Auto-withdraw is on — funds transfer automatically.
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col sm:items-end gap-2">
                          {wallet.bank_connected ? (
                            <Button
                              onClick={handleWithdraw}
                              disabled={withdrawing || wallet.available < wallet.min_withdrawal}
                              className="bg-white text-[#4da855] hover:bg-slate-50 rounded-full px-6 py-5 text-base font-semibold shadow-md disabled:opacity-60"
                              data-testid="withdraw-btn"
                            >
                              {withdrawing ? 'Processing…' : 'Withdraw to bank'}
                            </Button>
                          ) : (
                            <Button
                              onClick={handleConnectBank}
                              className="bg-white text-[#4da855] hover:bg-slate-50 rounded-full px-6 py-5 text-base font-semibold shadow-md"
                              data-testid="connect-bank-btn"
                            >
                              Connect bank to withdraw
                            </Button>
                          )}
                          {wallet.bank_connected && wallet.available > 0 && wallet.available < wallet.min_withdrawal && (
                            <p className="text-xs text-white/90">
                              Minimum ${wallet.min_withdrawal.toFixed(2)} to withdraw.
                            </p>
                          )}
                          {wallet.bank_connected && !wallet.stripe_connected && (
                            <p className="text-[10px] text-white/85">Manual bank transfer · 1–2 business days</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pending / Lifetime row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card data-testid="wallet-pending-card">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Pending</p>
                          <Clock className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900" data-testid="wallet-pending-amount">
                          ${wallet.pending.toFixed(2)}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          Clears {wallet.hold_hours}h after booking ends
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Lifetime earned</p>
                          <TrendingUp className="w-4 h-4 text-[#5ebc67]" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">${wallet.lifetime_earned.toFixed(2)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Withdrawn</p>
                          <Check className="w-4 h-4 text-blue-600" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">${wallet.lifetime_withdrawn.toFixed(2)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Auto-withdraw toggle */}
                  <Card data-testid="auto-withdraw-card">
                    <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-500" /> Auto-withdraw
                        </p>
                        <p className="text-sm text-slate-500 mt-1 max-w-xl">
                          When on, every ${wallet.min_withdrawal.toFixed(0)}+ you earn is transferred to your bank automatically as soon as it clears. Turn off if you'd rather withdraw manually.
                        </p>
                      </div>
                      <Switch
                        checked={wallet.auto_withdraw_enabled}
                        onCheckedChange={handleToggleAutoWithdraw}
                        disabled={savingSettings}
                        data-testid="auto-withdraw-toggle"
                      />
                    </CardContent>
                  </Card>

                  {/* Withdrawal history */}
                  <Card data-testid="withdrawal-history">
                    <CardHeader>
                      <CardTitle className="text-base">Withdrawal history</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {wallet.withdrawals.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center">No withdrawals yet.</p>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {wallet.withdrawals.map(w => (
                            <div key={w.withdrawal_id} className="py-3 flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-slate-900">${w.amount.toFixed(2)}</p>
                                <p className="text-xs text-slate-500">
                                  {format(new Date(w.created_at), 'MMM d, yyyy • h:mm a')}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-green-700 border-green-300">Paid</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Unauthorized Parking Report Dialog */}
      <Dialog open={reportDialog.open} onOpenChange={(open) => {
        if (!open) {
          setReportDialog({ open: false, listingId: null });
          setReportForm({ plate: '', description: '', notes: '' });
          setPlateCheckResult(null);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="report-parking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Report Unauthorized Parking
            </DialogTitle>
            <p className="text-sm text-slate-500">Check if a vehicle in your driveway has a valid booking</p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Plate Input + Check */}
            <div className="space-y-2">
              <Label htmlFor="plate">License Plate Number</Label>
              <div className="flex gap-2">
                <Input
                  id="plate"
                  placeholder="e.g. ABC123"
                  value={reportForm.plate}
                  onChange={(e) => setReportForm({ ...reportForm, plate: e.target.value.toUpperCase() })}
                  className="uppercase tracking-wider font-mono"
                  data-testid="plate-input"
                />
                <Button
                  variant="outline"
                  onClick={handleCheckPlate}
                  disabled={checkingPlate || !reportForm.plate.trim()}
                  data-testid="check-plate-btn"
                >
                  {checkingPlate ? (
                    <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Car className="w-4 h-4" />
                  )}
                  <span className="ml-1.5">Check</span>
                </Button>
              </div>
            </div>

            {/* Plate Check Result */}
            {plateCheckResult && (
              <div className={`p-3 rounded-lg text-sm ${
                plateCheckResult.authorized 
                  ? 'bg-green-50 border border-green-200 text-green-800' 
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`} data-testid="plate-check-result">
                {plateCheckResult.authorized ? (
                  <div className="space-y-1">
                    <p className="font-medium flex items-center gap-1">
                      <Shield className="w-4 h-4" /> Vehicle is Authorized
                    </p>
                    <p>Driver: {plateCheckResult.driver_name}</p>
                    <p>Booking: {plateCheckResult.booking_start && new Date(plateCheckResult.booking_start).toLocaleString()} — {plateCheckResult.booking_end && new Date(plateCheckResult.booking_end).toLocaleString()}</p>
                    <p>Registered Plate: {plateCheckResult.plate_on_booking}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="font-medium flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> Vehicle NOT Authorized
                    </p>
                    <p>{plateCheckResult.message}</p>
                    <p className="text-xs mt-1">{plateCheckResult.suggestion}</p>
                  </div>
                )}
              </div>
            )}

            {/* Show report fields only if plate is NOT authorized */}
            {plateCheckResult && !plateCheckResult.authorized && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="description">Vehicle Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="e.g. White Toyota Corolla"
                    value={reportForm.description}
                    onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                    data-testid="vehicle-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="e.g. Parked since 9am, blocking access"
                    value={reportForm.notes}
                    onChange={(e) => setReportForm({ ...reportForm, notes: e.target.value })}
                    data-testid="report-notes-input"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setReportDialog({ open: false, listingId: null });
                setReportForm({ plate: '', description: '', notes: '' });
                setPlateCheckResult(null);
              }}
              data-testid="cancel-report-btn"
            >
              Cancel
            </Button>
            {plateCheckResult && !plateCheckResult.authorized && (
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleReportUnauthorized}
                disabled={submittingReport}
                data-testid="submit-report-btn"
              >
                {submittingReport ? 'Reporting...' : 'Report Unauthorized'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostDashboard;
